import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Paper,
  useTheme
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { RefreshCw, HelpCircle as HelpIcon, TrendingUp, BarChart2 } from 'lucide-react';
import PricingStrategyService from '../services/AGTUps-product-services/pricingStrategyService';
import { ProductContext } from '../../../context/ProductContext';

// Definimos colores consistentes
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

// Creamos un theme personalizado
const createCustomTheme = (mode, actualMode) => createTheme({
  palette: { mode, actualMode },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
          color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
          '&:hover': {
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary,
          },
          '&:disabled': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
          },
        },
        text: {
          color: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[mode],
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          border: '1px solid',
          borderColor: actualMode === 'wallpaper'
            ? 'rgba(128, 128, 128, 0.3)'
            : (mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'),
          backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
          backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined
            }
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
          backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
          WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none'
        },
      },
    },
  },
  typography: {
    h5: { fontWeight: 600 },
    button: { textTransform: 'none' },
  },
});

const ReviewPricesShortcut = ({ open, onClose, product }) => {
  const theme = useTheme();
  const customTheme = createCustomTheme(theme.palette.mode, theme.palette.actualMode);
  const { products } = useContext(ProductContext);
  const [selectedProduct, setSelectedProduct] = useState(product || (products.length > 0 ? products[0] : null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pricingData, setPricingData] = useState(null);

  useEffect(() => {
    if (product) {
      setSelectedProduct(product);
    }
  }, [product]);

  const loadPricingData = useCallback(async () => {
    if (!selectedProduct?.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await PricingStrategyService.getPricingStrategiesByProduct(selectedProduct.id);
      if (response.success) {
        setPricingData(response.data);
      } else {
        throw new Error('Failed to load pricing strategy');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct]);
   
  useEffect(() => {
    if (open && selectedProduct?.id) {
      loadPricingData();
    }
  }, [open, selectedProduct, loadPricingData]);

  useEffect(() => {
    if (open && selectedProduct?.id) {
      loadPricingData();
    }
  }, [open, selectedProduct, loadPricingData]);

  const handleRecalculate = async () => {
    if (!selectedProduct?.id) return;
    setLoading(true);
    setError(null);
    try {
      const currentPrice = pricingData?.executiveSummary?.pricing?.current || 0;
      const params = { currentPrice, pricingModel: 'fixed', marginTarget: 40 };
      const response = await PricingStrategyService.calculateOptimalPrice(selectedProduct.id, params);
      if (response.success) {
        setPricingData((prev) => ({
          ...prev,
          aiAnalysis: {
            ...prev.aiAnalysis,
            currentAnalysis: {
              ...prev.aiAnalysis.currentAnalysis,
            }
          }
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const breakEvenUnits = pricingData?.executiveSummary?.breakEven?.units || 0;
  const totalSalesToday = pricingData?.executiveSummary?.salesMetrics?.totalSalesToday || 0;

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress
            size={40}
            sx={{ color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'rgba(128, 128, 128, 0.6)' }}
          />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: theme.palette.actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : undefined,
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
            '& .MuiAlert-icon': {
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined
            }
          }}
        >
          {error}
        </Alert>
      );
    }

    if (!selectedProduct) {
      return (
        <Alert
          severity="info"
          sx={{
            backgroundColor: theme.palette.actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : undefined,
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
            '& .MuiAlert-icon': {
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined
            }
          }}
        >
          No product available.
        </Alert>
      );
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(128, 128, 128, 0.05)' : 'rgba(128, 128, 128, 0.02)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <TrendingUp
                size={24}
                color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'rgba(128, 128, 128, 0.7)'}
              />
              <Typography variant="h6">{selectedProduct.name}</Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Break-even Units
                  </Typography>
                  <Typography variant="h4">
                    {breakEvenUnits}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Units Sold Today
                  </Typography>
                  <Typography variant="h4">
                    {totalSalesToday}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {breakEvenUnits > 0 && totalSalesToday > 0 && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChart2 size={20} />
              <Typography variant="body2" color="text.secondary">
                Progress: {((totalSalesToday / breakEvenUnits) * 100).toFixed(1)}% of break-even achieved
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pb: 2
          }}
        >
          <Typography variant="h5">
            Review Prices
          </Typography>
          <Tooltip title="Review and analyze product pricing">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {!product && products.length > 0 && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Product</InputLabel>
              <Select
                value={selectedProduct?.id || ''}
                label="Select Product"
                onChange={(e) => {
                  const prod = products.find(p => p.id === e.target.value);
                  setSelectedProduct(prod);
                }}
              >
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {renderContent()}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={onClose}
            sx={{ color: COLORS.text[theme.palette.mode] }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleRecalculate}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshCw size={20} />}
          >
            Recalculate
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ReviewPricesShortcut;